import { useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { shortDate } from '@/lib/format'
import { combine, useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Badge, Busy, Button, Card, cx, EmptyState, ErrorState } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Checkbox, Field, Input, Select, Switch } from '@/components/ui/form'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  CountrySummary,
  InvitedUser,
  Paged,
  PermissionSummary,
  RolePermissions,
  RoleSummary,
  UserDetail,
  UserListItem,
} from '@/types/api'

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 */
const DEFAULT_SORT = 'name'

export function Users() {
  const { can } = useAuth()
  const toast = useToast()
  const [inviting, setInviting] = useState(false)
  const [resending, setResending] = useState<string | undefined>()
  const [selected, setSelected] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('active')
  const [roleCode, setRoleCode] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  /**
   * Sends the activation email again. Codemax answers an address it already knows with the
   * account it already has, so this never creates a second one - it re-sends the link.
   */
  async function resendInvitation(user: UserListItem) {
    setResending(user.id)
    try {
      const resent = await api.post<InvitedUser>(`/users/${user.id}/resend-invitation`, {})
      // Codemax sends nothing when the account is already activated - which is the
      // ordinary case for anyone who holds a Codemax account for another application.
      // There is no invitation left to send them, and saying only that no email went
      // out reads as a fault and gets pressed again.
      toast.show(
        resent.activationEmailSent
          ? `Invitation sent again to ${user.email}.`
          : `No email sent: ${user.email} already has an activated Codemax account. Ask them to sign in with it - Codemax owns the password.`,
        resent.activationEmailSent ? 'success' : 'info',
      )
      list.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not resend the invitation', 'error')
    } finally {
      setResending(undefined)
    }
  }

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  const roles = useAsync<RoleSummary[]>((signal) => api.get<RoleSummary[]>('/roles', undefined, signal), [])

  const list = useAsync<Paged<UserListItem>>(
    (signal) =>
      api.get<Paged<UserListItem>>(
        '/users',
        {
          includeInactive: status !== 'active',
          search,
          roleCode: roleCode || undefined,
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    [status, search, roleCode, sort, page, pageSize],
  )

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Users"
        hint="Roles are a bundle of permissions; country access is granted separately. A user with no country access sees nothing."
        actions={
          can('user.invite') && (
            <Button variant="primary" onClick={() => setInviting(true)}>
              Invite user
            </Button>
          )
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'name', label: 'Name A–Z' },
          { value: '-name', label: 'Name Z–A' },
          { value: 'email', label: 'Email A–Z' },
          { value: '-lastlogin', label: 'Seen most recently' },
          { value: 'status', label: 'Deactivated first' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search name or email"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect label="Status" value={status} onChange={(next) => refine(() => setStatus(next))}>
          <option value="active">Active only</option>
          <option value="all">Active and deactivated</option>
        </FilterSelect>

        <FilterSelect label="Role" value={roleCode} onChange={(next) => refine(() => setRoleCode(next))}>
          <option value="">All roles</option>
          {roles.data?.map((role) => (
            <option key={role.code} value={role.code}>
              {role.name}
            </option>
          ))}
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {list.data.items.length === 0 ? (
              <EmptyState title="No users yet" hint="Invite the people who raise and approve quotations." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="name" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Name
                    </SortableTh>
                    <Th>Roles</Th>
                    <Th>Countries</Th>
                    <Th>Sign-in</Th>
                    <SortableTh field="lastlogin" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Last seen
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((user) => (
                    <Tr key={user.id} onClick={can('user.edit') ? () => setSelected(user.id) : undefined}>
                      <Td>
                        <span className="font-medium">{user.displayName}</span>
                        <span className="block text-[12px] text-muted">{user.email}</span>
                      </Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.length === 0 ? (
                            <Badge tone="warning">No role</Badge>
                          ) : (
                            user.roles.map((role) => <Badge key={role}>{role}</Badge>)
                          )}
                        </div>
                      </Td>
                      <Td>
                        {user.countries.length === 0 ? (
                          <Badge tone="warning">No access</Badge>
                        ) : (
                          <span className="font-mono text-[12px]">{user.countries.join(' ')}</span>
                        )}
                      </Td>
                      <Td className="text-muted">{user.authProvider}</Td>
                      <Td className="text-muted">
                        {!user.isActive ? (
                          <Badge tone="neutral">Deactivated</Badge>
                        ) : user.invitationPending ? (
                          // Never signed in, so the activation link is still outstanding. Said
                          // plainly, because "never" in a Last seen column reads as neglect
                          // rather than as an invitation nobody has acted on.
                          <span className="flex items-center gap-2">
                            <Badge tone="warning">Invitation pending</Badge>
                            {can('user.invite') && (
                              <Button
                                size="sm"
                                loading={resending === user.id}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  void resendInvitation(user)
                                }}
                              >
                                Resend
                              </Button>
                            )}
                          </span>
                        ) : (
                          shortDate(user.lastLoginAt)
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <InviteUser
        open={inviting}
        onClose={() => setInviting(false)}
        onInvited={(name, emailSent) => {
          setInviting(false)
          toast.show(
            emailSent
              ? `${name} invited. Codemax has emailed them a link to set their password.`
              : `${name} invited. No activation email was sent because they already have an activated Codemax account - ask them to sign in with it.`,
            emailSent ? 'success' : 'info',
          )
          list.reload()
        }}
      />

      <UserAccess
        userId={selected}
        onClose={() => setSelected(undefined)}
        onSaved={() => {
          setSelected(undefined)
          list.reload()
        }}
      />
    </>
  )
}

function InviteUser({
  open,
  onClose,
  onInvited,
}: {
  open: boolean
  onClose: () => void
  onInvited: (name: string, activationEmailSent: boolean) => void
}) {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState('SalesRep')
  const [countryIds, setCountryIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const roles = useAsync<RoleSummary[]>((signal) => api.get<RoleSummary[]>('/roles', undefined, signal), [])
  const countries = useAsync<CountrySummary[]>(
    (signal) => api.get<CountrySummary[]>('/meta/countries', undefined, signal),
    [],
  )

  // Both catalogues decide what the form offers, so the form waits for both. An invitation
  // sent with no country reaches somebody who can see nothing.
  const gate = combine(roles, countries)

  async function invite() {
    setSaving(true)
    try {
      const invited = await api.post<InvitedUser>('/users', {
        email,
        displayName,
        authProvider: 'EntraId',
        roleCodes: [role],
        countryIds,
        defaultCountryId: countryIds[0] ?? null,
      })

      onInvited(displayName, invited.activationEmailSent)
      setEmail('')
      setDisplayName('')
      setCountryIds([])
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not invite', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="Invite a user"
      description="Codemax creates their account and emails them a link to set their password. Their access here is what you grant below."
      onClose={onClose}
      loading={gate.loading}
      error={gate.error}
      onRetry={gate.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!email || !displayName || countryIds.length === 0}
            onClick={() => void invite()}
          >
            Send invitation
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Work email" required>
          {(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label="Full name" required>
          {(id) => <Input id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />}
        </Field>
        <Field label="Role">
          {(id) => (
            <Select id={id} value={role} onChange={(e) => setRole(e.target.value)}>
              {roles.data?.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-ink-2">
            Country access <span className="text-crit">*</span>
          </legend>
          {/* Without at least one grant the account can see nothing at all, so this is
              required at invitation rather than a step somebody forgets afterwards. */}
          <div className="flex flex-col gap-1.5">
            {countries.data?.map((country) => (
              <Checkbox
                key={country.id}
                label={`${country.name} (${country.isoCode})${country.isActive ? '' : ' - not yet active'}`}
                checked={countryIds.includes(country.id)}
                onChange={(event) =>
                  setCountryIds((current) =>
                    event.target.checked
                      ? [...current, country.id]
                      : current.filter((id) => id !== country.id),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
      </div>
    </Dialog>
  )
}

/** Two sets of ids, order ignored. What Save uses to decide whether a section is worth writing. */
function changed(next: readonly string[], loaded: readonly string[] | undefined): boolean {
  if (!loaded) {
    return true
  }

  return next.length !== loaded.length || [...next].sort().join() !== [...loaded].sort().join()
}

/**
 * Self-approval: the one permission this screen grants by name.
 *
 * Every other permission arrives in a role. This one is deliberately in no role but the
 * Owner's, because the rule is that a quotation is approved by somebody above the person
 * who raised it - a chain that lands on the preparer blocks the submit rather than letting
 * them sign it. Granting it to one person is an override on their record, audited like any
 * other, and it changes exactly one thing: their own step stops being refused. It still
 * skips no step of the chain.
 */
const SELF_APPROVAL = 'quotation.approve.self'

const APPROVAL_PERMISSIONS = ['quotation.approve.assigned', 'quotation.approve.any']

/** Whether this person holds it on their own record, rather than through a role. */
function granted(user: UserDetail) {
  return user.overrides.some(
    (item) => item.permissionCode === SELF_APPROVAL && item.effect === 'Grant',
  )
}

function UserAccess({
  userId,
  onClose,
  onSaved,
}: {
  userId: string | undefined
  onClose: () => void
  onSaved: () => void
}) {
  const { can } = useAuth()
  const toast = useToast()
  const [displayName, setDisplayName] = useState('')
  const [roles, setRoles] = useState<string[]>([])
  const [countryIds, setCountryIds] = useState<string[]>([])
  const [selfApproval, setSelfApproval] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [inspecting, setInspecting] = useState<RoleSummary | undefined>()

  const detail = useAsync<UserDetail | undefined>(
    (signal) =>
      userId
        ? api.get<UserDetail>(`/users/${userId}`, undefined, signal).then((loaded) => {
            setDisplayName(loaded.displayName)
            setRoles(loaded.roles)
            setCountryIds(loaded.countries.map((country) => country.countryId))
            setSelfApproval(granted(loaded))
            return loaded
          })
        : Promise.resolve(undefined),
    [userId],
  )

  const roleCatalogue = useAsync<RoleSummary[]>(
    (signal) => api.get<RoleSummary[]>('/roles', undefined, signal),
    [],
  )
  const countries = useAsync<CountrySummary[]>(
    (signal) => api.get<CountrySummary[]>('/meta/countries', undefined, signal),
    [],
  )

  // Correctness, not polish. Save writes the whole role and country set, and the boxes it
  // reads are filled in by the load - so a form drawn before the load finished, or after it
  // failed, would save an empty set and strip this person's access.
  const gate = combine(detail, roleCatalogue, countries)

  // An Owner may change their own roles and countries; nobody else may change their own.
  // The API answers that question on the record itself rather than leaving the screen to
  // work it out from a role, and reading it here keeps the boxes from offering an edit that
  // cannot land.
  const accessLocked = detail.data ? !detail.data.canEditAccess : false

  // Held through a role rather than through an override: the Owner's, in practice. The
  // switch shows it on and refuses to turn it off, because there is nothing on this record
  // to remove - the answer is the role, which is a box above it.
  const selfApprovalFromRole = detail.data
    ? detail.data.effectivePermissions.includes(SELF_APPROVAL) && !granted(detail.data)
    : false

  const holdsApproval =
    detail.data?.effectivePermissions.some((code) => APPROVAL_PERMISSIONS.includes(code)) ?? false

  /**
   * Turns access on or off without removing the person.
   *
   * <p>
   * Deactivating is what happens when somebody leaves: sign-in refuses them by name -
   * ARMS still authenticates every Codemax employee, so the refusal has to be QuoteHero's -
   * and everything they raised keeps their name on it. Deleting the record instead would
   * leave a quotation prepared by nobody.
   * </p>
   */
  async function setActive(isActive: boolean) {
    if (!userId) return

    setBusy(true)
    try {
      await api.post(`/users/${userId}/${isActive ? 'reactivate' : 'deactivate'}`, {})
      toast.show(isActive ? 'User reactivated' : 'User deactivated', 'success')
      detail.reload()
      onSaved()
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not change the status',
        'error',
      )
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (!userId) return

    setSaving(true)
    try {
      const renamed = displayName.trim()
      if (renamed && renamed !== detail.data?.displayName) {
        await api.patch(`/users/${userId}`, { displayName: renamed })
      }

      // Only what changed. Roles and countries are refused on your own account unless
      // you are the last Owner, so writing them unconditionally turned renaming yourself
      // into an error - after the rename had already saved.
      if (changed(roles, detail.data?.roles)) {
        await api.put(`/users/${userId}/roles`, { roleCodes: roles })
      }

      if (changed(countryIds, detail.data?.countries.map((country) => country.countryId))) {
        await api.put(`/users/${userId}/countries`, {
          countryIds,
          defaultCountryId: countryIds[0] ?? null,
        })
      }

      // The whole override set every time, because that endpoint replaces it. Everything
      // this screen does not offer is carried through untouched rather than dropped.
      if (detail.data && selfApproval !== granted(detail.data)) {
        const others = detail.data.overrides.filter(
          (item) => item.permissionCode !== SELF_APPROVAL,
        )

        await api.put(`/users/${userId}/permissions`, {
          overrides: selfApproval
            ? [
                ...others,
                {
                  permissionCode: SELF_APPROVAL,
                  effect: 'Grant',
                  reason: 'Self-approval granted on the user record',
                },
              ]
            : others,
        })
      }

      toast.show('User updated', 'success')
      onSaved()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save this user', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={userId !== undefined}
      title={detail.data?.displayName ?? 'User'}
      description={
        detail.data && !detail.data.isActive
          ? 'This person is deactivated and cannot sign in. Their roles and countries are kept for when they come back.'
          : 'Roles bundle permissions; country access decides what those permissions can reach.'
      }
      onClose={onClose}
      wide={inspecting !== undefined}
      loading={gate.loading}
      error={gate.error}
      onRetry={gate.reload}
      footer={
        <>
          {/* On the left of the footer, away from Save: it takes effect immediately and
              is not part of what Save writes. */}
          {can('user.deactivate') && detail.data && (
            <Button
              size="sm"
              variant={detail.data.isActive ? 'danger' : 'secondary'}
              loading={busy}
              className="mr-auto"
              onClick={() => void setActive(!detail.data!.isActive)}
            >
              {detail.data.isActive ? 'Deactivate' : 'Reactivate'}
            </Button>
          )}

          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div
        className={cx(
          'grid gap-5',
          // The panel opens beside the form rather than over it: the point of reading a
          // role's access is to decide whether to tick its box, and a panel covering the
          // box makes that two operations instead of one.
          inspecting && 'md:grid-cols-[minmax(0,1fr)_18rem]',
        )}
      >
      <div className="flex flex-col gap-5">
        <Field
          label="Name"
          hint="How this person is shown throughout QuoteHero. Their sign-in and email stay with ARMS - changing this does not change either."
        >
          {(fieldId) => (
            <Input
              id={fieldId}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          )}
        </Field>

        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-ink-2">Roles</legend>
          <div className="flex flex-col gap-1.5">
            {roleCatalogue.data?.map((role) => (
              <div key={role.code} className="flex items-center justify-between gap-3">
                <Checkbox
                  label={role.name}
                  checked={roles.includes(role.code)}
                  disabled={accessLocked}
                  onChange={(event) =>
                    setRoles((current) =>
                      event.target.checked
                        ? [...current, role.code]
                        : current.filter((code) => code !== role.code),
                    )
                  }
                />

                {/* The count is the way in. "50 permissions" is the question somebody has
                    already asked by the time they have read it, so it is the thing to click. */}
                <button
                  type="button"
                  aria-expanded={inspecting?.code === role.code}
                  onClick={() =>
                    setInspecting((current) => (current?.code === role.code ? undefined : role))
                  }
                  className={cx(
                    'shrink-0 rounded-md px-2 py-0.5 text-[12px] transition-colors',
                    inspecting?.code === role.code
                      ? 'bg-accent-wash text-accent'
                      : 'text-muted hover:bg-sunken hover:text-ink',
                  )}
                >
                  {role.permissionCount} permissions
                </button>
              </div>
            ))}
          </div>
          {accessLocked && (
            <p className="mt-1.5 text-[12px] text-muted">
              Only an Owner may change their own roles and countries. Ask an Owner to make
              this change - everything else here is yours to edit.
            </p>
          )}
        </fieldset>

        <fieldset>
          <legend className="mb-1.5 text-[13px] font-medium text-ink-2">Country access</legend>
          <div className="flex flex-col gap-1.5">
            {countries.data?.map((country) => (
              <Checkbox
                key={country.id}
                // Inactive countries are offered on purpose - a country is granted before
                // it is activated, because its terms document is uploaded through the
                // country selector and the selector lists only granted countries. Saying
                // so beside the box stops the grant looking like a mistake.
                label={`${country.name} (${country.isoCode})${country.isActive ? '' : ' - not yet active'}`}
                checked={countryIds.includes(country.id)}
                disabled={accessLocked}
                onChange={(event) =>
                  setCountryIds((current) =>
                    event.target.checked
                      ? [...current, country.id]
                      : current.filter((id) => id !== country.id),
                  )
                }
              />
            ))}
          </div>
        </fieldset>

        {detail.data && (
          <div className="border-t border-line-soft pt-4">
            <Switch
              label="Self-approval"
              hint={
                selfApprovalFromRole
                  ? 'Comes with the Owner role: there is nobody above them, so their own quotation could otherwise never leave draft.'
                  : 'Lets this person approve a quotation they raised themselves. Everybody else goes to a superior.'
              }
              checked={selfApprovalFromRole || selfApproval}
              disabled={accessLocked || selfApprovalFromRole || !can('user.permission.override')}
              onChange={setSelfApproval}
            />

            {/* Granting it to somebody who cannot act on a step at all resolves the chain
                onto them and then refuses them the decision - a quotation stuck between the
                two. Said here rather than discovered at submit. */}
            {(selfApprovalFromRole || selfApproval) && !holdsApproval && (
              <p className="mt-2 text-[12px] text-warn">
                On its own this does nothing: they hold no approval permission today, so no
                step - their own included - can be decided by them. Give them a role that
                approves.
              </p>
            )}
          </div>
        )}
      </div>

        {inspecting && <RoleAccess role={inspecting} onClose={() => setInspecting(undefined)} />}
      </div>
    </Dialog>
  )
}

/**
 * What a role grants, beside the box that grants it.
 *
 * <p>
 * A role is a bundle of permissions, and the list said how many were in it without saying
 * which. Deciding whether somebody should be a Manager means knowing that a Manager can
 * exceed a discount threshold and approve what is assigned to them - not that a Manager has
 * fifty of something.
 * </p>
 *
 * Grouped by category and scrollable, because fifty dotted codes in one column is a list
 * nobody reads to the end of.
 */
function RoleAccess({ role, onClose }: { role: RoleSummary; onClose: () => void }) {
  const granted = useAsync<RolePermissions>(
    (signal) => api.get<RolePermissions>(`/roles/${role.id}/permissions`, undefined, signal),
    [role.id],
  )

  const groups = new Map<string, PermissionSummary[]>()
  for (const permission of granted.data?.permissions ?? []) {
    groups.set(permission.category, [...(groups.get(permission.category) ?? []), permission])
  }

  return (
    <aside className="rounded-md border border-line-soft bg-sunken/40">
      <div className="flex items-start justify-between gap-2 border-b border-line-soft px-3 py-2">
        <div>
          <p className="text-[13px] font-medium">{role.name}</p>
          <p className="text-[12px] text-muted">
            {role.description ?? `${role.permissionCount} permissions`}
          </p>
        </div>

        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="shrink-0 rounded-md px-1.5 text-muted hover:bg-surface hover:text-ink"
        >
          ×
        </button>
      </div>

      {/* Its own scroll, so the form beside it does not move while this is read. */}
      <div className="max-h-[46vh] overflow-y-auto px-3 py-2">
        {granted.loading && <p className="py-4 text-center text-[12px] text-muted">Loading...</p>}

        {granted.error && (
          <p className="py-2 text-[12px] text-crit">{granted.error.detail ?? granted.error.title}</p>
        )}

        {granted.data?.permissions.length === 0 && (
          <p className="py-2 text-[12px] text-muted">This role grants nothing.</p>
        )}

        {[...groups.entries()].map(([category, permissions]) => (
          <div key={category} className="mb-3 last:mb-0">
            <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              {category}
            </p>

            <ul className="flex flex-col gap-1">
              {permissions.map((permission) => (
                <li key={permission.code}>
                  <span className="text-[12.5px] text-ink-2">{permission.description}</span>
                  {/* The code as well as the sentence: it is what an endpoint declares and
                      what a support question quotes back. */}
                  <span className="block font-mono text-[10.5px] text-faint">{permission.code}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </aside>
  )
}
